import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Sparkles, ScanText, Wand2, Coins, Clock } from "lucide-react";
import { getToolsUsageDashboard } from "@/lib/api/tools-usage.functions";
import { getToolsSeoStatus } from "@/lib/api/tools-seo.functions";

export const Route = createFileRoute("/_authenticated/portal/tools-dashboard")({
  component: ToolsDashboardPage,
});

const ICONS: Record<string, any> = {
  "tools-humanise": Sparkles,
  "tools-ocr": ScanText,
  "tools-paraphrase": Wand2,
};

const HREFS: Record<string, string> = {
  "tools-humanise": "/tools/humaniser",
  "tools-ocr": "/tools/ocr",
  "tools-paraphrase": "/tools/paraphraser",
};

function ToolsDashboardPage() {
  const fetchFn = useServerFn(getToolsUsageDashboard);
  const fetchSeoStatus = useServerFn(getToolsSeoStatus);
  const q = useQuery({
    queryKey: ["tools-usage-dashboard"],
    queryFn: () => fetchFn(),
    refetchInterval: 30_000,
  });
  const seo = useQuery({
    queryKey: ["tools-seo-status"],
    queryFn: () => fetchSeoStatus(),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="space-y-8">
      <header>
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Credits & usage</p>
        <h2 className="mt-1 font-display text-2xl text-navy">Tools dashboard</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Track your free daily allowance and paid credit balance across every AI tool.
        </p>
      </header>

      {q.isLoading ? (
        <Loader2 className="h-5 w-5 animate-spin text-navy" />
      ) : q.data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Coins className="h-4 w-4 text-gold" /> Credit balance
              </div>
              <p className="mt-2 font-display text-3xl text-navy">
                {q.data.admin ? "Unlimited" : q.data.balance}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Paid credits used once your daily free allowance is exhausted.
              </p>
              <Link
                to={"/credits" as any}
                className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline"
              >
                Top up credits →
              </Link>
            </div>
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                <Clock className="h-4 w-4 text-gold" /> Free allowance resets
              </div>
              <p className="mt-2 font-display text-lg text-navy">
                {new Date(q.data.resetsAt).toLocaleString()}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                All daily counters below reset at 00:00 UTC.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {q.data.tools.map((t) => {
              const Icon = ICONS[t.scope] ?? Sparkles;
              const remaining = Math.max(0, t.limit - t.used);
              const pct = Math.min(100, (t.used / t.limit) * 100);
              return (
                <div key={t.scope} className="rounded-lg border border-border bg-card p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="rounded-md bg-navy/5 p-2 text-navy">
                        <Icon className="h-5 w-5" />
                      </span>
                      <div>
                        <h3 className="font-display text-lg text-navy">{t.label}</h3>
                        <p className="text-xs text-muted-foreground">{t.note}</p>
                      </div>
                    </div>
                    <Link
                      to={HREFS[t.scope] as any}
                      className="text-xs font-semibold text-navy hover:underline"
                    >
                      Open →
                    </Link>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      Free today: <span className="font-semibold text-navy">{remaining}</span> of{" "}
                      {t.limit} left
                    </span>
                    <span className="text-muted-foreground">
                      Then <span className="font-semibold text-navy">{t.creditCost} credit</span> per use
                    </span>
                  </div>
                  <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full bg-navy transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          <section className="rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg text-navy">Tools SEO & structured data</h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  Public tool pages are discovered from route files and added to the sitemap automatically.
                </p>
              </div>
              <span className="rounded-full bg-navy/5 px-3 py-1 text-xs font-semibold text-navy">
                Sitemap: automatic
              </span>
            </div>
            {seo.isLoading ? (
              <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking structured data…
              </div>
            ) : seo.data ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-xs">
                  <span className="font-medium text-navy">Free Tools collection schema</span>
                  <span className={seo.data.collection.valid ? "text-emerald-700" : "text-destructive"}>
                    {seo.data.collection.valid ? "Valid" : seo.data.collection.errors.join(", ")}
                  </span>
                </div>
                {seo.data.tools.map((tool) => (
                  <div
                    key={tool.path}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/40 px-3 py-2 text-xs"
                  >
                    <span className="font-medium text-navy">{tool.name}</span>
                    <span className="text-muted-foreground">{tool.path}</span>
                    <span className={tool.valid ? "text-emerald-700" : "text-destructive"}>
                      {tool.valid ? "Valid SoftwareApplication JSON-LD" : tool.errors.join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-xs text-destructive">Couldn't verify structured data right now.</p>
            )}
          </section>
        </>
      ) : (
        <p className="text-sm text-destructive">Couldn't load usage. Try again in a moment.</p>
      )}
    </div>
  );
}
